import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { type DspRestrictionModes, dspRestrictionModes } from '@openx/types/organization';
import { useCallback, useMemo } from 'react';
import { type Control, type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../constants';
import type { OrganizationFormData } from '../../../types';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext';

export type DspRestrictionFormInputsProps = {
  index: number;
  fieldId: string;
  control: Control<OrganizationFormData>;
  disabled?: boolean;
  dspOptions: Record<string, any>;
  dspLoading: boolean;
  remove: (index: number) => void;
  canRemove: boolean;
  dspFieldOverride?: string;
  isInitial?: boolean;
  initialDspId?: string | null;
  initialSeatIds?: string[];
  showSeatIdOnlyWhenDspSelected?: boolean;
  noSeatsInfoMessage?: string;
  enableSelectDeselectAll?: boolean;
  initialDspRestrictionMode?: DspRestrictionModes | null;
};

export const DspRestrictionFormInputs = ({
  index,
  fieldId,
  control,
  disabled,
  dspOptions,
  dspLoading,
  remove,
  canRemove,
  dspFieldOverride,
  isInitial = false,
  initialDspId = null,
  initialSeatIds = [],
  showSeatIdOnlyWhenDspSelected = false,
  noSeatsInfoMessage,
  enableSelectDeselectAll = false,
  initialDspRestrictionMode,
}: DspRestrictionFormInputsProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<OrganizationFormData>();

  const dspField = (dspFieldOverride ||
    `${organizationFields.DSP_RESTRICTION}[${index}].dsp_id`) as Path<OrganizationFormData>;
  const seatField = `${organizationFields.DSP_RESTRICTION}[${index}].seat_ids` as Path<OrganizationFormData>;

  const observableSelectedDspId = useWatch({ control, name: dspField }) as string | null;
  const { useDspSeatsFetch } = useOrganizationContext();
  const { mapById: seatOptions, loading: seatLoading } = useDspSeatsFetch(observableSelectedDspId);
  const dspList = useMemo(() => Object.values(dspOptions), [dspOptions]);
  const selectedDspObj = useMemo(
    () => dspList.find(dsp => dsp.id === observableSelectedDspId) || null,
    [dspList, observableSelectedDspId]
  );

  const isInitialDspSelected = initialDspId !== null && observableSelectedDspId === initialDspId;
  const isDspAllowModeEnabled = initialDspRestrictionMode === dspRestrictionModes.ALLOW;
  const isDspFrozen =
    disabled || (isInitial && isDspAllowModeEnabled && initialDspRestrictionMode === dspRestrictionModes.ALLOW);
  const isDspRemovable = canRemove && (!isInitial || initialDspRestrictionMode !== dspRestrictionModes.ALLOW);

  const renderValue = useCallback(
    (value, getItemProps) => {
      return value.map((option, index) => {
        const { key, onDelete, ...itemProps } = getItemProps({ index });
        const isFrozen = isInitialDspSelected && initialSeatIds.includes(option) && isDspAllowModeEnabled;
        return (
          <Chip
            key={key}
            label={seatOptions[option]?.name || t('N / A')}
            {...itemProps}
            onDelete={isFrozen ? undefined : onDelete}
            sx={{ opacity: isFrozen ? 0.5 : 1 }}
          />
        );
      });
    },
    [isInitialDspSelected, initialSeatIds, seatOptions, t, isDspAllowModeEnabled]
  );

  const handleSeatChange = useCallback(
    (_event, newValue) => {
      if (isInitialDspSelected && isDspAllowModeEnabled) {
        const merged = [...new Set([...initialSeatIds, ...newValue])];
        setValue(seatField, merged);
      } else {
        setValue(seatField, newValue);
      }
    },
    [isInitialDspSelected, initialSeatIds, setValue, seatField, isDspAllowModeEnabled]
  );

  return (
    <Grid
      container
      spacing={2}
      key={fieldId}
      sx={{
        alignItems: 'center',
        mb: 1,
      }}
    >
      <Grid size={{ xs: isDspRemovable ? 11 : 12 }}>
        <RHFAutoCompleteVirtualize
          name={dspField}
          textFieldProps={{
            label: t('DSP'),
            placeholder: t('[ Select DSP ]'),
          }}
          options={dspList}
          getOptionLabel={option => option?.name || t('N / A')}
          isOptionEqualToValue={(option, value) => option?.id === value}
          value={selectedDspObj}
          onChange={(_, option) => {
            setValue(dspField, option?.id || null);
            setValue(seatField, []);
          }}
          loading={dspLoading}
          disabled={isDspFrozen}
          data-test={`dsp-dropdown-${index}`}
        />
      </Grid>
      {isDspRemovable && (
        <Grid
          size={{ xs: 1 }}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}
        >
          <IconButton onClick={() => remove(index)} disabled={disabled} data-test={`remove-dsp-${index}`}>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Grid>
      )}
      {(!showSeatIdOnlyWhenDspSelected || observableSelectedDspId) && (
        <Grid size={{ xs: isDspRemovable ? 11 : 12 }}>
          {noSeatsInfoMessage && !seatLoading && Object.keys(seatOptions).length === 0 ? (
            <Alert severity="info" data-test={`no-seats-info-${index}`}>
              {noSeatsInfoMessage}
            </Alert>
          ) : (
            <RHFAutoCompleteVirtualize
              name={seatField}
              control={control}
              textFieldProps={{
                label: t('Seat ID'),
                placeholder: t('[ Select Seat ID(s) ]'),
              }}
              options={Object.keys(seatOptions)}
              getOptionLabel={option => seatOptions[option]?.name || t('N / A')}
              multiple
              loading={seatLoading}
              disabled={!observableSelectedDspId}
              onChange={handleSeatChange}
              renderValue={renderValue}
              data-test={`seat-dropdown-${index}`}
              showSelectAllOptions={enableSelectDeselectAll}
            />
          )}
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ marginBottom: 1, marginTop: 1 }} data-test="divider" />
      </Grid>
    </Grid>
  );
};
